import type { CycleDay } from "./types";

const DAY_MS = 86_400_000;

export function localDateKey(date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function dateFromKey(value: string): Date {
  return new Date(`${value}T12:00:00`);
}

export function addDays(value: string, days: number): string {
  const date = dateFromKey(value);
  date.setDate(date.getDate() + days);
  return localDateKey(date);
}

export function differenceInDays(later: string, earlier: string): number {
  const left = dateFromKey(later);
  const right = dateFromKey(earlier);
  return Math.round(
    (Date.UTC(left.getFullYear(), left.getMonth(), left.getDate()) -
      Date.UTC(right.getFullYear(), right.getMonth(), right.getDate())) /
      DAY_MS,
  );
}

export function formatDate(
  value: string,
  options: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" },
): string {
  return new Intl.DateTimeFormat("en-AU", options).format(dateFromKey(value));
}

export function isBleeding(day: Pick<CycleDay, "flow">): boolean {
  return day.flow === "light" || day.flow === "medium" || day.flow === "heavy";
}

export function periodStarts(days: CycleDay[]): string[] {
  const bleedingDates = [...new Set(
    days.filter(isBleeding).map((day) => day.date),
  )].sort();
  return bleedingDates.filter(
    (date, index) =>
      index === 0 || differenceInDays(date, bleedingDates[index - 1]) > 3,
  );
}

export function median(values: number[]): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((left, right) => left - right);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[middle]
    : Math.round((sorted[middle - 1] + sorted[middle]) / 2);
}

export interface CycleSummary {
  latestStart: string | null;
  cycleDay: number | null;
  usualLength: number | null;
  recentCycleLengths: number[];
  nextPeriod: string | null;
  estimateKind: "personal" | "rough" | "none";
  loggedPeriodDays: number;
}

export function cycleSummary(
  days: CycleDay[],
  today = localDateKey(),
): CycleSummary {
  const starts = periodStarts(days).filter((date) => date <= today);
  const lengths = starts
    .slice(1)
    .map((date, index) => differenceInDays(date, starts[index]))
    .filter((length) => length >= 15 && length <= 60);
  const usualLength = median(lengths);
  const latestStart = starts.at(-1) ?? null;
  const elapsed = latestStart ? differenceInDays(today, latestStart) : null;
  const cycleDay =
    elapsed !== null && elapsed >= 0 && elapsed <= 90 ? elapsed + 1 : null;
  const estimateLength = usualLength ?? (latestStart ? 28 : null);
  const nextPeriod =
    latestStart && estimateLength ? addDays(latestStart, estimateLength) : null;

  return {
    latestStart,
    cycleDay,
    usualLength,
    recentCycleLengths: lengths.slice(-4),
    nextPeriod,
    estimateKind: usualLength ? "personal" : latestStart ? "rough" : "none",
    loggedPeriodDays: days.filter(isBleeding).length,
  };
}

export function calendarDays(month: Date): Array<{
  key: string;
  day: number;
  inMonth: boolean;
}> {
  const first = new Date(month.getFullYear(), month.getMonth(), 1, 12);
  const mondayOffset = (first.getDay() + 6) % 7;
  const start = new Date(first);
  start.setDate(1 - mondayOffset);
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return {
      key: localDateKey(date),
      day: date.getDate(),
      inMonth: date.getMonth() === month.getMonth(),
    };
  });
}

export function capitalise(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
